import { FunctionComponent, useState } from "react";
import { useOrderCreateCtx } from "../../../../../context_providers/order_create_context";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../../new_components/common/drawer_modal";

import UploadAttachmentWidget from "./upload_attachment_widget";

interface AttachmentSectionProps {}

const AttachmentSection: FunctionComponent<AttachmentSectionProps> = () => {
  const { attachments, setAttachments } = useOrderCreateCtx();
  const [showAdd, setShowAdd] = useState(false);

  return (
    <>
      {showAdd && (
        <DrawerModal
          location={DrawerModalLoaction.right}
          onClose={() => setShowAdd(false)}
          show={showAdd}
          title="Add Attachment"
        >
          <UploadAttachmentWidget
            onClose={() => setShowAdd(false)}
            onSubmit={(data) => {
              setAttachments((o) => [...o, data]);
              setShowAdd(false);
            }}
          />
        </DrawerModal>
      )}
      <div
        id="order-create-attachments-section"
        className="flex flex-wrap items-center justify-between gap-2"
      >
        <h3 className="text-base sm:text-lg font-semibold">Attachments</h3>
        <button
          id="order-create-attachment-add-btn"
          onClick={() => setShowAdd(true)}
          className="shrink-0 px-3 py-1 rounded bg-myPrimaryColor text-white text-sm"
        >
          Add
        </button>
      </div>
      {attachments.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mt-4">
          {attachments.map((data, i) => (
            <div
              key={i}
              id={`order-create-attachment-card-${i}`}
              className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200"
            >
              {/* Preview Section */}
              {/* Preview Section */}
              <div className="relative h-40 sm:h-48 bg-gray-50 overflow-hidden">
                {data.file.type.startsWith("image") ? (
                  <img
                    src={URL.createObjectURL(data.file)}
                    alt={data.file.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
                    <div className="text-center">
                      <svg
                        className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-500 mb-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-xs font-medium text-gray-600 uppercase">
                        {data.file.type === "application/pdf"
                          ? "PDF"
                          : "Document"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-200"></div>
              </div>

              {/* Info Section */}
              <div className="p-3 space-y-2">
                {/* Category Badge */}
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 truncate max-w-[70%]">
                    {data.category}
                  </span>
                  <span className="text-xs text-gray-400 shrink-0 whitespace-nowrap">
                    {(data.file.size / 1024).toFixed(1)} KB
                  </span>
                </div>

                {/* File Name */}
                <p
                  className="text-xs text-gray-600 truncate"
                  title={data.file.name}
                >
                  {data.file.name}
                </p>

                {/* Delete Button */}
                <button
                  id={`order-create-attachment-delete-btn-${i}`}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to delete this attachment?",
                      )
                    )
                      setAttachments((o) => o.filter((f, j) => i !== j));
                  }}
                  className="w-full mt-2 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors duration-150 flex items-center justify-center gap-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center text-center h-64 sm:h-96 mt-4 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg
            className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mb-3 sm:mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-base sm:text-lg font-semibold text-gray-600 mb-1">
            No attachments yet
          </h3>
          <p className="text-xs sm:text-sm text-gray-400">
            Click "Add" button to upload attachments
          </p>
        </div>
      )}
    </>
  );
};

export default AttachmentSection;
